import { DOCUMENT, Injectable, computed, effect, inject, signal } from '@angular/core';
import { Subject } from 'rxjs';

import { getValuesAsString } from '@common/helpers/formatter';

import type { ThemeSettings } from '@sites/site-theme/src/enums/theme-settings.enum';
import { getThemeButtonPresets } from '@sites/site-theme/src/config/presets/button-presets.model';
import { getThemeColorPresets } from '@sites/site-theme/src/config/presets/color-presets.model';
import { getThemeFontPresets } from '@sites/site-theme/src/config/presets/font-presets.model';
import {
    withCustomButtonPreset,
    withCustomColorPreset,
    withCustomFontPreset,
} from '@sites/site-theme/src/utils/selection';
import type { ThemeSelectedButtons, ThemeSelectedColors, ThemeSelectedFonts } from '@sites/site-theme/src/types';
import { formatThemeVariables } from '@sites/site-theme/src/utils/formatter';
import { compactThemeFonts } from '@sites/site-theme/src/helpers/font-css';
import { withPaletteFontColor } from '@sites/site-theme/src/utils/font-color';
import { initialThemeSettings } from '@sites/site-theme/src/utils/initial-theme';

import type { ThemeChange, ThemePresetsState, ThemeSettingsState } from './theme.types';

/**
 * How long a run of edits is allowed to settle before the preset lists are rebuilt.
 *
 * Only the preset lists are debounced. Editing a font fires on every keystroke of the settings
 * form, and rebuilding the list — which prepends a synthetic "custom" entry once the values stop
 * matching any shipped preset — is worth doing once the user stops. The values themselves are not
 * debounced: those drive the live preview.
 */
const PRESET_REBUILD_DELAY_MS = 500;

/**
 * The theme, as the editor holds it while a site is open.
 *
 * **This replaced an NgRx feature store of 28 files.** That store had four slices — config, presets,
 * settings, values — wired together by six effects whose only job was to keep them consistent: one
 * turned `changeX` into `updateX`, another recomputed the CSS variables from the settings after
 * every one of those, a third rebuilt the preset lists. None of that was state management; it was
 * derivation, spelled out by hand because a reducer cannot derive.
 *
 * Everything readable here is a signal. The one exception is `changes$`, and it is not state: see
 * its comment.
 */
@Injectable({ providedIn: 'root' })
export class ThemeStateService {
    private readonly document = inject(DOCUMENT);

    private readonly settingsState = signal<ThemeSettingsState>({});
    private readonly presetsState = signal<ThemePresetsState>({});
    private readonly loadedState = signal(false);
    private readonly editedState = signal(false);
    private readonly activeSettingState = signal<ThemeSettings | null>(null);

    private readonly changesSubject = new Subject<ThemeChange>();
    private presetRebuildTimer: ReturnType<typeof setTimeout> | null = null;
    private styleElement: HTMLStyleElement | null = null;

    public readonly settings = this.settingsState.asReadonly();
    public readonly presets = this.presetsState.asReadonly();
    /** `true` once `initTheme` has run — the panels stay hidden until then. */
    public readonly loaded = this.loadedState.asReadonly();
    /**
     * Whether the user has changed the theme since it was loaded. Until then a save may write back
     * the stored theme as it was rather than what was read from it — see the editor's save.
     */
    public readonly edited = this.editedState.asReadonly();
    /** Which settings panel is open, or `null` for none. */
    public readonly activeSetting = this.activeSettingState.asReadonly();

    public readonly colors = computed(() => this.settingsState().colors);
    public readonly fonts = computed(() => this.settingsState().fonts);
    public readonly buttons = computed(() => this.settingsState().buttons);

    /**
     * The values themselves, defaulted to empty.
     *
     * A component reading these binds them straight into a colour picker or a preset strip, and
     * every one of those wants a list. Defaulting here rather than at each of the ~30 call sites is
     * the difference between one decision and thirty.
     */
    public readonly selectedColors = computed(() => this.colors()?.selected ?? []);
    public readonly selectedFonts = computed(() => this.fonts()?.selected ?? []);
    public readonly selectedButtons = computed(() => this.buttons()?.selected ?? []);

    public readonly selectedColorIndex = computed(() => this.colors()?.selectedIndex ?? null);
    public readonly selectedFontIndex = computed(() => this.fonts()?.selectedIndex ?? null);
    public readonly selectedButtonIndex = computed(() => this.buttons()?.selectedIndex ?? null);

    public readonly colorPresets = computed(() => this.presetsState().colors ?? []);
    public readonly fontPresets = computed(() => this.presetsState().fonts ?? []);
    public readonly buttonPresets = computed(() => this.presetsState().buttons ?? []);

    /**
     * The CSS custom properties the current theme resolves to.
     *
     * Derived, not stored. The old store kept these in a fourth slice and MERGED each recomputation
     * into the last (`{ ...state, ...values }`), so a variable that stopped being produced kept its
     * previous value for the rest of the session. `formatTheme` always emits the full set, so
     * replacing it wholesale is both simpler and what the stylesheet actually wants.
     */
    public readonly values = computed<Record<string, string>>(() =>
        this.loadedState() ? formatThemeVariables(this.settingsState()) : {}
    );

    /**
     * User-made changes to the three parts — the one thing here that is not a signal.
     *
     * A signal answers "what is it now"; this answers "something was just edited", and those are
     * different questions. The editor's autosave and its undo history both need the second one: a
     * value that is set back to what it already was is still an edit, and a signal would not report
     * it. Both consumers are NgRx effects, which take an observable directly.
     *
     * `before` travels with the event so history does not have to read the state at exactly the
     * right instant — see `ThemeChange`.
     */
    public readonly changes$ = this.changesSubject.asObservable();

    constructor() {
        effect(() => this.writeCssVariables(this.values()));
    }

    /**
     * Builds the initial presets and settings from what the page had stored.
     *
     * The three `if (config.colors)` guards this used to carry are gone with the strategy layer:
     * they were always true, because the only variant here has all three parts.
     */
    public initTheme(themeSettings: ThemeSettingsState = {}, isNewSite: boolean = false): void {
        this.settingsState.set(initialThemeSettings(themeSettings, isNewSite));
        this.syncPresets();
        this.loadedState.set(true);
        this.editedState.set(false);
    }

    /**
     * Puts back the parts an undo or redo hands over — no change is announced, it is not an edit —
     * and rebuilds the preset strips, so the site's own row matches what is restored.
     */
    public restore(parts: ThemeSettingsState): void {
        this.settingsState.update((settings) => ({ ...settings, ...parts }));
        this.syncPresets();
    }

    /**
     * Rebuilds the three preset strips from the current selection at once — on load and after an
     * undo, where a site's own palette, buttons or fonts have to show up as the first row straight
     * away rather than after its next edit.
     */
    private syncPresets(): void {
        if (this.presetRebuildTimer !== null) {
            clearTimeout(this.presetRebuildTimer);
            this.presetRebuildTimer = null;
        }

        const { colors, fonts, buttons } = this.settingsState();

        this.presetsState.set({
            colors: colors ? withCustomColorPreset(colors) : getThemeColorPresets(),
            fonts: fonts ? withCustomFontPreset(fonts) : getThemeFontPresets(),
            buttons: buttons ? withCustomButtonPreset(buttons) : getThemeButtonPresets(),
        });
    }

    /**
     * A user edit: stores the new selection, announces it, and schedules a preset rebuild.
     *
     * `set*` below is the same write without those last two — it is what a load or an undo uses,
     * where there is no edit to record and the presets are already right.
     */
    public changeColors(colors: ThemeSelectedColors): void {
        const before = this.colors();

        this.setColors(colors);

        // Picking a different colour THEME re-points the text at its `dark`. Editing a single colour
        // (no index) or re-applying the same theme — which is what cancelling the panel does — keeps
        // any colour the user set on a font by hand.
        const fonts = this.fonts();
        const recolorsFonts = fonts && colors.selectedIndex != null && colors.selectedIndex !== before?.selectedIndex;

        if (recolorsFonts) {
            this.setFonts(withPaletteFontColor(fonts, colors.selected));
        }

        this.editedState.set(true);
        this.changesSubject.next({
            before: { colors: before, ...(recolorsFonts && { fonts }) },
            after: { colors, ...(recolorsFonts && { fonts: this.fonts() }) },
        });
        this.schedulePresetRebuild(() => ({ colors: withCustomColorPreset(colors) }));
    }

    public changeFonts(changed: ThemeSelectedFonts): void {
        const before = this.fonts();
        const fonts = compactThemeFonts(changed);

        this.setFonts(fonts);
        this.editedState.set(true);
        this.changesSubject.next({ before: { fonts: before }, after: { fonts } });
        this.schedulePresetRebuild(() => ({ fonts: withCustomFontPreset(fonts) }));
    }

    public changeButtons(buttons: ThemeSelectedButtons): void {
        const before = this.buttons();

        this.setButtons(buttons);
        this.editedState.set(true);
        this.changesSubject.next({ before: { buttons: before }, after: { buttons } });
        this.schedulePresetRebuild(() => ({ buttons: withCustomButtonPreset(buttons) }));
    }

    public setColors(colors: ThemeSelectedColors): void {
        this.settingsState.update((settings) => ({ ...settings, colors }));
    }

    public setFonts(fonts: ThemeSelectedFonts): void {
        this.settingsState.update((settings) => ({ ...settings, fonts: compactThemeFonts(fonts) }));
    }

    public setButtons(buttons: ThemeSelectedButtons): void {
        this.settingsState.update((settings) => ({ ...settings, buttons }));
    }

    public setFontsSynchronization(syncWithDesktop: ThemeSelectedFonts['syncWithDesktop']): void {
        this.settingsState.update((settings) => ({
            ...settings,
            fonts: { ...settings.fonts!, syncWithDesktop },
        }));
    }

    public setActiveSetting(value: ThemeSettings): void {
        this.activeSettingState.set(value);
    }

    public unselectActiveSetting(): void {
        this.activeSettingState.set(null);
    }

    private schedulePresetRebuild(build: () => ThemePresetsState): void {
        if (this.presetRebuildTimer !== null) {
            clearTimeout(this.presetRebuildTimer);
        }

        this.presetRebuildTimer = setTimeout(() => {
            this.presetRebuildTimer = null;
            this.presetsState.update((presets) => ({ ...presets, ...build() }));
        }, PRESET_REBUILD_DELAY_MS);
    }

    /**
     * Publishes the variables as a `:root` rule in a `<style>` of our own.
     *
     * A single element, rewritten in place, rather than inline styles on the host: the widgets read
     * these through `var(--…)` from anywhere in the tree, including inside previews that are not
     * children of any themed element.
     */
    private writeCssVariables(values: Record<string, string>): void {
        const head = this.document.querySelector('head');

        if (!head) {
            return;
        }

        if (!this.styleElement) {
            this.styleElement = this.document.createElement('style');
            head.appendChild(this.styleElement);
        }

        this.styleElement.innerHTML = `:root { ${getValuesAsString(values)} }`;
    }
}
